import React, { useState, useEffect, useRef, useId } from "react";
import { searchUsers } from "../../cheques/api/chequesApi";
import { GlobalTextField, GlobalButton } from "../../../global/forms/GlobalFormFields";
import "./UserSearch.css";

const UserSearch = ({
  label,
  required = false,
  error,
  baseAlertId,
  value,
  onChange,
  /** Called with full user row when a result is picked (includes department_name from search API). */
  onUserSelect,
  selectedUserLabel,
  placeholder = "Search name or ID...",
  disabled = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const searchRef = useRef(null);
  const inputId = useId();

  // Sync from controlled `value` / `selectedUserLabel` (string-safe id compare).
  useEffect(() => {
    const valueStr = value != null && value !== "" ? String(value) : "";
    if (!valueStr) {
      setSelectedUser(null);
      return;
    }

    setSelectedUser((prev) => {
      if (prev && String(prev.id) === valueStr) {
        if (
          typeof selectedUserLabel === "string" &&
          selectedUserLabel.trim() &&
          prev.name !== selectedUserLabel
        ) {
          return { ...prev, name: selectedUserLabel };
        }
        return prev;
      }
      return {
        id: valueStr,
        name:
          typeof selectedUserLabel === "string" && selectedUserLabel.trim()
            ? selectedUserLabel
            : "Selected user",
      };
    });
  }, [value, selectedUserLabel]);

  useEffect(() => {
    const delay = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setSearching(true);
        try {
          const resp = await searchUsers(searchQuery);
          setSearchResults(resp?.data || []);
          setShowResults(true);
        } catch {
          setSearchResults([]);
        } finally {
          setSearching(false);
        }
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    onChange(user.id);
    onUserSelect?.(user);
    setSearchQuery("");
    setShowResults(false);
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    onChange("");
    setSearchQuery("");
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (selectedUser) {
      setSelectedUser(null);
      onChange("");
    }
  };

  return (
    <div className="gff-field user-search-container" ref={searchRef}>
      <label
        htmlFor={inputId}
        className={`gff-label${error ? " gff-label--error" : ""}`}
      >
        {label}
        {required ? " *" : null}
      </label>
      
      {selectedUser ? (
        <div className="user-search-selected">
          <div className="user-search-selected-info">
            <div className="user-search-avatar">
              {selectedUser.name ? selectedUser.name.charAt(0).toUpperCase() : "?"}
            </div>
            <div className="user-search-details">
              <div className="user-search-name">{selectedUser.name}</div>
            </div>
          </div>
          <GlobalButton
            variant="ghost"
            size="sm"
            onClick={handleClearSelection}
            disabled={disabled}
          >
            Change
          </GlobalButton>
        </div>
      ) : (
        <div className="user-search-input-wrapper">
          <GlobalTextField
            id={inputId}
            hideLabel
            placeholder={placeholder}
            value={searchQuery}
            onChange={handleSearchChange}
            error={error}
            baseAlertId={baseAlertId}
            disabled={disabled}
            required={required}
            describedByIds="user-search-hint"
          />
          
          {searching && (
            <div className="user-search-loading">
              Searching...
            </div>
          )}

          {showResults && searchResults.length > 0 && (
            <div className="user-search-results">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="user-search-result-item"
                  onClick={() => handleUserSelect(user)}
                >
                  <div className="user-search-result-info">
                    <div className="user-search-result-name">{user.name}</div>
                    {user.department_name ? (
                      <div className="user-search-result-details">{user.department_name}</div>
                    ) : null}
                  </div>
                  <div className="user-search-result-select">
                    Select
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
            
      <div id="user-search-hint" className="sr-only">
        Type at least 2 characters to search for users
      </div>
    </div>
  );
};

export default UserSearch;
